import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QrCode, Copy, CheckCircle, Clock, ArrowLeft, CreditCard, PixLogo, Lock } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Card number formatting
const formatCardNumber = (value) => {
  const v = value.replace(/\D/g, "").slice(0, 16);
  const parts = [];
  for (let i = 0; i < v.length; i += 4) parts.push(v.slice(i, i + 4));
  return parts.join(" ");
};

// Expiry formatting
const formatExpiry = (value) => {
  const v = value.replace(/\D/g, "").slice(0, 4);
  if (v.length >= 3) return v.slice(0, 2) + "/" + v.slice(2);
  return v;
};

// Luhn algorithm validation
const luhnCheck = (num) => {
  const digits = num.replace(/\D/g, "");
  if (digits.length < 13) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alt) { n *= 2; if (n > 9) n -= 9; }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

// Detect card brand
const getCardBrand = (num) => {
  const n = num.replace(/\D/g, "");
  if (/^4/.test(n)) return "Visa";
  if (/^5[1-5]/.test(n)) return "Mastercard";
  if (/^(636368|438935|504175|451416|636297|5067|4576|4011|506[67])/.test(n)) return "Elo";
  if (/^3[47]/.test(n)) return "Amex";
  if (/^(606282|3841)/.test(n)) return "Hipercard";
  return "";
};

const PixPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const { itemId, itemType, itemInfo } = location.state || {};

  const [step, setStep] = useState("form");
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [cpf, setCpf] = useState("");
  const [pixData, setPixData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Card fields
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [cardName, setCardName] = useState("");
  const [installments, setInstallments] = useState(1);
  const [cardBrand, setCardBrand] = useState("");

  // Scheduling
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const needsSchedule = itemType === "product";

  useEffect(() => {
    if (!itemId || !itemInfo) navigate("/dashboard", { replace: true });
  }, [itemId, itemInfo, navigate]);

  if (!itemId || !itemInfo) return null;

  const sendWhatsAppNotification = (data, type) => {
    const adminWhatsApp = "5521984317457";
    const methodLabel = type === "card" ? "CARTÃO" : data.amount === 0 ? "GRÁTIS" : "PIX";
    const msg = `✅ *${data.amount === 0 ? "NOVA RESERVA" : "PAGAMENTO " + methodLabel + " APROVADO"} - MARABU OPEN CLUB*\n\n` +
      `👤 Nome: ${data.payer_name || user?.name}\n` +
      `📧 Email: ${data.payer_email || user?.email}\n` +
      `📱 Telefone: ${data.payer_phone || user?.phone}\n` +
      `🎫 Item: ${data.item_name}\n` +
      `💰 Valor: ${data.amount === 0 ? "GRÁTIS" : `R$ ${data.amount?.toFixed(2)}`}\n` +
      `💳 Método: ${methodLabel}\n` +
      (data.ticket_code ? `🎟️ Código: ${data.ticket_code}\n` : "") +
      `🕐 Data: ${new Date().toLocaleString("pt-BR")}\n\n` +
      `🎉 Confirmado!`;
    window.open(`https://wa.me/${adminWhatsApp}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // PIX payment
  const handlePixSubmit = async (e) => {
    e.preventDefault();
    if (needsSchedule && !schedDate) { toast.error("Selecione uma data para agendamento"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/pix/create`, {
        item_id: itemId, item_type: itemType, payer_document: cpf || "00000000000",
        scheduled_date: schedDate, scheduled_time: schedTime
      }, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true, timeout: 30000 });

      if (response.data.free || response.data.status === "approved") {
        setPixData(response.data);
        setStep("success");
        sendWhatsAppNotification(response.data, "pix");
      } else {
        setPixData(response.data);
        setStep("qrcode");
        startStatusPolling(response.data.payment_id);
      }
    } catch (error) {
      if (error.code === "ECONNABORTED") toast.error("Tempo esgotado. Verifique sua conexão e tente novamente.");
      else if (!error.response) toast.error("Sem conexão com o servidor. Verifique sua internet.");
      else toast.error(error.response?.data?.detail || "Erro ao processar PIX");
    } finally {
      setLoading(false);
    }
  };

  // Card payment
  const handleCardSubmit = async (e) => {
    e.preventDefault();
    const rawNum = cardNumber.replace(/\s/g, "");
    if (!luhnCheck(rawNum)) { toast.error("Número do cartão inválido. Verifique os dígitos."); return; }
    if (cardExpiry.length < 5) { toast.error("Validade inválida"); return; }
    if (cardCvv.length < 3) { toast.error("CVV inválido"); return; }
    if (!cardName.trim()) { toast.error("Nome do titular obrigatório"); return; }
    if (!cpf.trim()) { toast.error("CPF obrigatório"); return; }
    if (needsSchedule && !schedDate) { toast.error("Selecione uma data para agendamento"); return; }

    const [expMonth, expYear] = cardExpiry.split("/");
    const fullYear = parseInt(expYear) < 100 ? 2000 + parseInt(expYear) : parseInt(expYear);

    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/card/create`, {
        item_id: itemId, item_type: itemType,
        card_number: rawNum,
        expiration_month: parseInt(expMonth),
        expiration_year: fullYear,
        security_code: cardCvv,
        cardholder_name: cardName,
        payer_document: cpf,
        installments: installments,
        scheduled_date: schedDate,
        scheduled_time: schedTime
      }, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true, timeout: 30000 });

      if (response.data.status === "approved") {
        setPixData(response.data);
        setStep("success");
        sendWhatsAppNotification(response.data, "card");
      } else if (response.data.status === "in_process" || response.data.status === "pending") {
        setPixData(response.data);
        setStep("success");
        toast.info("Pagamento em análise. Você receberá a confirmação em breve.");
      } else {
        const detail = response.data.status_detail || "rejected";
        const msgs = {
          cc_rejected_call_for_authorize: "Ligue para a operadora do cartão para autorizar.",
          cc_rejected_insufficient_amount: "Saldo insuficiente no cartão.",
          cc_rejected_bad_filled_security_code: "CVV incorreto.",
          cc_rejected_bad_filled_date: "Data de validade incorreta.",
          cc_rejected_bad_filled_other: "Dados do cartão incorretos.",
        };
        toast.error(msgs[detail] || "Pagamento recusado. Tente outro cartão.");
      }
    } catch (error) {
      if (error.code === "ECONNABORTED") toast.error("Tempo esgotado. Verifique sua conexão.");
      else if (!error.response) toast.error("Sem conexão. Verifique sua internet.");
      else toast.error(error.response?.data?.detail || "Erro ao processar cartão. Verifique os dados.");
    } finally {
      setLoading(false);
    }
  };

  const startStatusPolling = (paymentId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/payments/pix/${paymentId}/status`, {
          headers: getAuthHeaders(), withCredentials: true
        });
        if (response.data.status === "approved") {
          clearInterval(interval);
          setPixData(response.data);
          setStep("success");
          sendWhatsAppNotification(response.data, "pix");
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 3000);
    setTimeout(() => clearInterval(interval), 300000);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(pixData.qr_code);
    toast.success("Código PIX copiado!");
  };

  const isFree = itemInfo.price === 0;

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4" data-testid="payment-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button onClick={() => navigate("/dashboard")} variant="ghost" className="rounded-full" data-testid="back-button">
            <ArrowLeft size={24} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#042F2E]">Pagamento</h1>
            <p className="text-[#115E59]">{itemInfo.title} - {isFree ? "GRÁTIS" : `R$ ${itemInfo.price.toFixed(2)}`}</p>
          </div>
        </div>

        {/* FORM STEP */}
        {step === "form" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Payment method selector - only for paid items */}
            {!isFree && (
              <div className="flex gap-3 mb-6" data-testid="payment-method-selector">
                <button
                  onClick={() => setPaymentMethod("pix")}
                  className={`flex-1 flex items-center justify-center gap-3 py-4 rounded-2xl border-2 font-bold transition-all ${
                    paymentMethod === "pix"
                      ? "bg-[#042F2E] text-white border-[#042F2E] shadow-lg"
                      : "bg-white text-[#115E59] border-[#E4DFD3] hover:border-[#0891B2]"
                  }`}
                  data-testid="method-pix"
                >
                  <PixLogo size={24} weight="fill" />
                  PIX
                </button>
                <button
                  onClick={() => setPaymentMethod("card")}
                  className={`flex-1 flex items-center justify-center gap-3 py-4 rounded-2xl border-2 font-bold transition-all ${
                    paymentMethod === "card"
                      ? "bg-[#042F2E] text-white border-[#042F2E] shadow-lg"
                      : "bg-white text-[#115E59] border-[#E4DFD3] hover:border-[#0891B2]"
                  }`}
                  data-testid="method-card"
                >
                  <CreditCard size={24} weight="fill" />
                  Cartão
                </button>
              </div>
            )}

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E4DFD3]">
              <h2 className="text-2xl font-bold text-[#042F2E] mb-2">
                {isFree ? "Confirmar Reserva" : paymentMethod === "pix" ? "Pagar com PIX" : "Pagar com Cartão"}
              </h2>
              <p className="text-[#115E59] mb-6">
                {isFree
                  ? <><strong>{user?.name}</strong>, confirme sua reserva gratuita</>
                  : <>Pagando como: <strong>{user?.name}</strong> ({user?.email})</>
                }
              </p>

              <AnimatePresence mode="wait">
                {/* FREE or PIX form */}
                {(isFree || paymentMethod === "pix") && (
                  <motion.form key="pix" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={handlePixSubmit} className="space-y-4">
                    {needsSchedule && (
                      <div className="bg-[#F4F1EA] rounded-xl p-4 space-y-3">
                        <p className="text-sm font-bold text-[#042F2E]">Agendar Data e Horário</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs font-bold text-[#115E59]">Data</Label>
                            <Input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)}
                              min={new Date().toISOString().split("T")[0]} className="rounded-xl border-[#E4DFD3]"
                              required data-testid="sched-date-pix" />
                          </div>
                          <div>
                            <Label className="text-xs font-bold text-[#115E59]">Horário</Label>
                            <select value={schedTime} onChange={(e) => setSchedTime(e.target.value)}
                              className="w-full rounded-xl border border-[#E4DFD3] px-3 py-2 text-sm" data-testid="sched-time-pix">
                              <option value="">Qualquer</option>
                              {["08:00","09:00","10:00","11:00","14:00","15:00","16:00","17:00","18:00","19:00"].map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                    {!isFree && (
                      <div>
                        <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">CPF</Label>
                        <Input type="text" placeholder="000.000.000-00" value={cpf} onChange={(e) => setCpf(e.target.value)}
                          className="rounded-xl border-[#E4DFD3]" required data-testid="pix-cpf-input" />
                      </div>
                    )}
                    <Button type="submit" disabled={loading}
                      className={`w-full font-bold rounded-full py-6 text-lg mt-4 text-white ${isFree ? "bg-[#10B981] hover:bg-[#059669]" : "bg-[#0891B2] hover:bg-[#0E7490]"}`}
                      data-testid="generate-pix-button">
                      {loading ? "Processando..." : isFree ? "Confirmar Reserva Grátis" : `Pagar R$ ${itemInfo.price.toFixed(2)} via PIX`}
                    </Button>
                  </motion.form>
                )}

                {/* CARD form */}
                {!isFree && paymentMethod === "card" && (
                  <motion.form key="card" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} onSubmit={handleCardSubmit} className="space-y-4">
                    {needsSchedule && (
                      <div className="bg-[#F4F1EA] rounded-xl p-4 space-y-3">
                        <p className="text-sm font-bold text-[#042F2E]">Agendar Data e Horário</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs font-bold text-[#115E59]">Data</Label>
                            <Input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)}
                              min={new Date().toISOString().split("T")[0]} className="rounded-xl border-[#E4DFD3]"
                              required data-testid="sched-date-card" />
                          </div>
                          <div>
                            <Label className="text-xs font-bold text-[#115E59]">Horário</Label>
                            <select value={schedTime} onChange={(e) => setSchedTime(e.target.value)}
                              className="w-full rounded-xl border border-[#E4DFD3] px-3 py-2 text-sm" data-testid="sched-time-card">
                              <option value="">Qualquer</option>
                              {["08:00","09:00","10:00","11:00","14:00","15:00","16:00","17:00","18:00","19:00"].map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Número do Cartão</Label>
                        {cardBrand && <span className="text-xs font-bold text-[#0891B2] bg-blue-50 px-2 py-0.5 rounded-full" data-testid="card-brand">{cardBrand}</span>}
                      </div>
                      <div className="relative">
                        <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-[#115E59]" size={20} weight="duotone" />
                        <Input type="text" placeholder="0000 0000 0000 0000" value={cardNumber}
                          onChange={(e) => { const v = formatCardNumber(e.target.value); setCardNumber(v); setCardBrand(getCardBrand(v)); }}
                          className="pl-12 rounded-xl border-[#E4DFD3] font-mono" maxLength={19} required data-testid="card-number-input" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Validade</Label>
                        <Input type="text" placeholder="MM/AA" value={cardExpiry}
                          onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                          className="rounded-xl border-[#E4DFD3] font-mono" maxLength={5} required data-testid="card-expiry-input" />
                      </div>
                      <div>
                        <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">CVV</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#115E59]" size={18} weight="duotone" />
                          <Input type="text" placeholder="123" value={cardCvv}
                            onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                            className="pl-10 rounded-xl border-[#E4DFD3] font-mono" maxLength={4} required data-testid="card-cvv-input" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Nome no Cartão</Label>
                      <Input type="text" placeholder="NOME COMO ESTÁ NO CARTÃO" value={cardName}
                        onChange={(e) => setCardName(e.target.value.toUpperCase())}
                        className="rounded-xl border-[#E4DFD3] uppercase" required data-testid="card-name-input" />
                    </div>

                    <div>
                      <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">CPF do Titular</Label>
                      <Input type="text" placeholder="000.000.000-00" value={cpf}
                        onChange={(e) => setCpf(e.target.value)}
                        className="rounded-xl border-[#E4DFD3]" required data-testid="card-cpf-input" />
                    </div>

                    {itemInfo.price >= 50 && (
                      <div>
                        <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Parcelas</Label>
                        <select value={installments} onChange={(e) => setInstallments(parseInt(e.target.value))}
                          className="w-full rounded-xl border border-[#E4DFD3] px-4 py-3 text-sm bg-white text-[#042F2E] font-semibold"
                          data-testid="card-installments-select">
                          <option value={1}>1x de R$ {itemInfo.price.toFixed(2)} (sem juros)</option>
                          {itemInfo.price >= 100 && <option value={2}>2x de R$ {(itemInfo.price / 2).toFixed(2)} (sem juros)</option>}
                          {itemInfo.price >= 150 && <option value={3}>3x de R$ {(itemInfo.price / 3).toFixed(2)} (sem juros)</option>}
                          {itemInfo.price >= 300 && <option value={6}>6x de R$ {(itemInfo.price / 6).toFixed(2)}</option>}
                          {itemInfo.price >= 600 && <option value={12}>12x de R$ {(itemInfo.price / 12).toFixed(2)}</option>}
                        </select>
                      </div>
                    )}

                    <Button type="submit" disabled={loading}
                      className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-6 text-lg mt-2"
                      data-testid="pay-card-button">
                      {loading ? "Processando..." : `Pagar R$ ${itemInfo.price.toFixed(2)} no Cartão`}
                    </Button>

                    <div className="flex items-center justify-center gap-2 text-xs text-[#115E59] mt-2">
                      <Lock size={14} weight="fill" />
                      <span>Pagamento seguro via Mercado Pago</span>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* QR Code Step (PIX only) */}
        {step === "qrcode" && pixData && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-8 shadow-sm border border-[#E4DFD3]" data-testid="qr-code-section">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-[#FFF4E6] text-[#FF5A26] px-4 py-2 rounded-full mb-6">
                <Clock size={20} weight="fill" />
                <span className="font-bold">Aguardando Pagamento</span>
              </div>
              <h2 className="text-2xl font-bold text-[#042F2E] mb-2">Escaneie o QR Code</h2>
              <p className="text-[#115E59] mb-8">Use o app do seu banco para pagar via PIX</p>
              <div className="bg-white p-8 rounded-2xl border-4 border-[#E4DFD3] inline-block mb-6">
                <img src={`data:image/png;base64,${pixData.qr_code_base64}`} alt="QR Code PIX" className="w-64 h-64" data-testid="qr-code-image" />
              </div>
              <div className="bg-[#F4F1EA] rounded-xl p-4 mb-6">
                <p className="text-sm text-[#115E59] font-bold mb-2">Ou copie o código PIX:</p>
                <div className="flex gap-2">
                  <Input value={pixData.qr_code} readOnly className="font-mono text-xs rounded-xl" />
                  <Button onClick={copyToClipboard} className="bg-[#0891B2] hover:bg-[#0E7490] rounded-xl" data-testid="copy-pix-button">
                    <Copy size={20} />
                  </Button>
                </div>
              </div>
              <p className="font-bold text-[#042F2E]">Valor: R$ {pixData.amount.toFixed(2)}</p>
            </div>
          </motion.div>
        )}

        {/* Success Step */}
        {step === "success" && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-12 shadow-sm border border-[#E4DFD3] text-center" data-testid="success-section">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }} className="inline-block mb-6">
              <CheckCircle size={80} weight="fill" className="text-[#10B981]" />
            </motion.div>
            <h2 className="text-3xl font-bold text-[#042F2E] mb-4">Pagamento Confirmado!</h2>
            <p className="text-lg text-[#115E59] mb-8">Seu ingresso foi gerado com sucesso!</p>

            {pixData?.ticket_qrcode && (
              <div className="mb-8">
                <div className="bg-[#F4F1EA] rounded-2xl p-6 inline-block">
                  <h3 className="text-xl font-bold text-[#042F2E] mb-4">Seu Ingresso</h3>
                  <div className="bg-white p-6 rounded-xl border-4 border-[#FF5A26]">
                    <img src={`data:image/png;base64,${pixData.ticket_qrcode}`} alt="QR Code Ingresso" className="w-64 h-64 mx-auto" data-testid="ticket-qrcode" />
                  </div>
                  <p className="mt-3 font-bold text-[#FF5A26]">{pixData.ticket_code}</p>
                  <p className="text-sm text-[#115E59] mt-1">Apresente este QR Code na entrada</p>
                </div>
              </div>
            )}

            <div className="bg-[#F4F1EA] rounded-xl p-6 mb-6 text-left max-w-md mx-auto">
              <h3 className="font-bold text-[#042F2E] mb-3">Detalhes da Compra:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#115E59]">Item:</span><span className="font-semibold text-[#042F2E]">{pixData?.item_name}</span></div>
                <div className="flex justify-between"><span className="text-[#115E59]">Valor:</span><span className="font-semibold text-[#042F2E]">{pixData?.amount === 0 ? "GRÁTIS" : `R$ ${pixData?.amount?.toFixed(2)}`}</span></div>
                <div className="flex justify-between"><span className="text-[#115E59]">Nome:</span><span className="font-semibold text-[#042F2E]">{pixData?.payer_name || user?.name}</span></div>
                {pixData?.installments > 1 && (
                  <div className="flex justify-between"><span className="text-[#115E59]">Parcelas:</span><span className="font-semibold text-[#042F2E]">{pixData.installments}x</span></div>
                )}
              </div>
            </div>

            <Button onClick={() => navigate("/dashboard")} className="bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full px-10 py-6 text-lg" data-testid="back-dashboard-btn">
              Voltar ao Dashboard
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PixPayment;
